import React from "react"
import { Chip } from "@mui/material"
import { connectCurrentRefinements } from "react-instantsearch-dom"
import {
	CurrentRefinementsProvided,
	CurrentRefinementsExposed,
	Refinement,
	SearchState,
	RefinementValue,
} from "react-instantsearch-core"

import { centsToEuros } from "@secureo/common/utils/forex/convertCurrencyFraction"

// https://www.algolia.com/doc/api-reference/widgets/clear-refinements/react/#provided-props
const getRefinementValuesToClearWithoutInitialRefinements = (
	refinements: Refinement[],
	virtualRefinements: SearchState = {},
) => {
	const initialRefinementsList = virtualRefinements?.refinementList ?? {}
	const initialRefinementAttributes = Object.keys(initialRefinementsList)

	const refinementValuesToClear = refinements.filter(
		({ attribute }) => !initialRefinementAttributes.includes(attribute),
	)

	return refinementValuesToClear
}

const getRefinementType = (
	currentRefinement: Refinement["currentRefinement"] | { min: number; max: number },
) => {
	if (typeof currentRefinement === "string" || Array.isArray(currentRefinement)) {
		return "checkbox"
	} else {
		return "range"
	}
}

const CheckboxRefinement = ({ attribute, refinement, refine }: CheckboxRefinementProps) => {
	const { items } = refinement

	return (
		<>
			{items.map((item) => {
				const { value } = item

				const label = `${attribute}: ${item.label}`

				switch (attribute) {
					case "manufacturer":
						return (
							<Chip
								key={label}
								label={`Hersteller: ${item.label}`}
								onDelete={(event) => {
									event.preventDefault()
									refine(value)
								}}
								style={{
									marginRight: 5,
									background: "#E7EFF6",
									fontSize: 11,
								}}
							/>
						)
				}

				return (
					<Chip
						key={label}
						label={label}
						onDelete={(event) => {
							event.preventDefault()
							refine(value)
						}}
						style={{
							marginRight: 5,
							background: "#E7EFF6",
							fontSize: 11,
						}}
					/>
				)
			})}
		</>
	)
}

interface CheckboxRefinementProps {
	attribute: string
	refinement: Refinement
	refine: CurrentRefinementsProvided["refine"]
}

const RangeRefinement = ({ attribute, currentRefinement, refine, value }: RangeRefinementProps) => {
	const { min, max } = currentRefinement

	const isPriceAttribute = attribute.includes("Cents")
	const formattedMin = (isPriceAttribute ? centsToEuros(min) : min).toLocaleString()
	const formattedMax = (isPriceAttribute ? centsToEuros(max) : max).toLocaleString()
	const suffix = isPriceAttribute ? "€" : ""

	const label = `${attribute}: ${formattedMin}${suffix} - ${formattedMax}${suffix}`

	switch (attribute) {
		case "grossPriceCents":
			return (
				<Chip
					label={`Preis: ${formattedMin}${suffix} - ${formattedMax}${suffix}`}
					onDelete={(event) => {
						event.preventDefault()
						refine(value)
					}}
					key={label}
					style={{
						marginRight: 5,
						background: "#E7EFF6",
						fontSize: 11,
					}}
				/>
			)
	}

	return (
		<Chip
			label={label}
			onDelete={(event) => {
				event.preventDefault()
				refine(value)
			}}
			key={label}
			style={{
				marginRight: 5,
				background: "#E7EFF6",
				fontSize: 11,
			}}
		/>
	)
}

interface RangeRefinementProps {
	attribute: string
	currentRefinement: {
		min: number
		max: number
	}
	refine: CurrentRefinementsProvided["refine"]
	value: RefinementValue
}

const SelectedFilters = ({
	items: refinements,
	refine,
	virtualRefinements,
}: Props & CurrentRefinementsProvided) => {
	const nonVirtualRefinementsValues = getRefinementValuesToClearWithoutInitialRefinements(
		refinements,
		virtualRefinements,
	)

	const resetFilters = () => {
		// Typings are incorrect for refine function
		refine(nonVirtualRefinementsValues as any)
	}

	return (
		<>
			<div
				style={{
					display: "flex",
					alignItems: "center",
					flexWrap: "wrap",
					marginBottom: -10,
				}}
			>
				{nonVirtualRefinementsValues.length > 1 && (
					<Chip
						label="Filter zurücksetzen"
						onClick={resetFilters}
						color="secondary"
						style={{
							marginRight: 5,
							fontSize: 11,
						}}
					/>
				)}

				{refinements.map((refinement) => {
					const { label, attribute, currentRefinement, value } = refinement

					const virtualRefinementValue =
						virtualRefinements?.refinementList?.[attribute] ?? null
					const attributeIsVirtuallyRefined = !!virtualRefinementValue

					if (attributeIsVirtuallyRefined) return null

					const refinementType = getRefinementType(currentRefinement)

					const key = `${attribute}-${label}`

					if (refinementType === "checkbox")
						return (
							<CheckboxRefinement
								key={key}
								attribute={attribute}
								refinement={refinement}
								refine={refine}
							/>
						)

					if (refinementType === "range")
						return (
							<RangeRefinement
								key={key}
								attribute={attribute}
								refine={refine}
								value={value}
								// Typings are incorrect for range refinements
								currentRefinement={currentRefinement as any}
							/>
						)

					return null
				})}
			</div>
		</>
	)
}

const CustomCurrentRefinements: React.ComponentClass<CurrentRefinementsExposed & Props> =
	connectCurrentRefinements(SelectedFilters)

interface Props {
	virtualRefinements?: SearchState
}

export default CustomCurrentRefinements
